import { TeButton, TeContainer, TeModal } from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useNotification } from '@topcon/topcon-utils';
import { Statuses } from '@topcon/tuicl';

import {
  BusinessPartnerBody,
  ServiceAgreementBody,
  ServiceAgreementTermsEnum,
} from '../../../api/generated/pm';
import { useAvailableAgreements, AvailableAgreementsFilters } from './dealers.queries';
import SelectDealerStep from './SelectDealerStep';
import SelectAgreementTypeStep from './SelectAgreementTypeStep';
import useServiceAgreement from '../../../hooks/useServiceAgreement';
import ReviewAgreementStep from '../../../components/ServiceAgreement/Customer/ReviewAgreementStep';
import useDebounce from '../../../hooks/useDebounce';
import { useErrorDrawer } from '../../../providers/ErrorDrawerContext/error-drawer.context';
const MODAL_MAX_WIDTH = 800;

interface AddDealerModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (agreement?: ServiceAgreementBody) => void;
}

enum StepEnum {
  SelectDealer,
  SelectAgreement,
  TermsAndConditions,
}

type StepKey = keyof typeof StepEnum;

function AddDealerModal({ open, onClose, onSubmit }: AddDealerModalProps) {
  const { t } = useTranslation();
  const { showError } = useErrorDrawer();
  const [step, setStep] = useState<StepEnum>(StepEnum.SelectDealer);
  const [businessPartner, setBusinessPartner] = useState<BusinessPartnerBody | undefined>();
  const [agreementType, setAgreementType] = useState<ServiceAgreementTermsEnum>(
    ServiceAgreementTermsEnum.FULL_SERVICE
  );

  const [filters, setFilters] = useState<AvailableAgreementsFilters>({});

  const debouncedFilters = useDebounce<AvailableAgreementsFilters>(filters);

  const { data: availableProviders, isLoading } = useAvailableAgreements(debouncedFilters);
  const notificationService = useNotification();
  const [eulaDisclaimerAccepted, setEulaDisclaimerAccepted] = useState<boolean>(false);
  const [isAgreementAcknowledged, setIsAgreementAcknowledged] = useState<boolean>(false);

  const closeModal = () => {
    if (isPendingCreateServiceAgreementWithProvider) return;
    setStep(StepEnum.SelectDealer);
    setAgreementType(ServiceAgreementTermsEnum.FULL_SERVICE);
    setIsAgreementAcknowledged(false);
    setBusinessPartner(undefined);
    onClose();
  };

  const { createServiceAgreementWithProvider, isPendingCreateServiceAgreementWithProvider } =
    useServiceAgreement();

  const headerTitle = t('dealers.createModal.header', { ns: 'common' });

  const currentStep = Object.values(StepEnum)
    .filter((v) => isNaN(+v))
    .findIndex((s) => StepEnum[s as StepKey] === step);

  const totalSteps = Object.values(StepEnum).filter((v) => isNaN(+v)).length;
  const footerText = t('step.count', { current: currentStep + 1, total: totalSteps });

  const handleFiltersChange = (update: Partial<AvailableAgreementsFilters>) => {
    setFilters((prev) => ({ ...prev, ...update }));
  };

  const handleNavigationBack = () => {
    if (step === StepEnum.SelectAgreement) {
      setStep(StepEnum.SelectDealer);
      setBusinessPartner(undefined);
    }
  };

  const handleCreateServiceAgreement = async () => {
    if (!businessPartner) return;
    try {
      const agreement = await createServiceAgreementWithProvider(businessPartner, agreementType);
      onSubmit(agreement);
      closeModal();
    } catch (e: unknown) {
      if (showError(e)) {
        return;
      }
      notificationService.addNotification(
        t('dealers.messages.addedError'),
        t('dealers.messages.addedErrorDescription', { name: businessPartner.name }),
        Statuses.error
      );
      return Promise.reject(e);
    }
  };

  const dealers = availableProviders?.items as BusinessPartnerBody[] | undefined;

  const handleNextStep = () => {
    if (step === StepEnum.SelectDealer) {
      setStep(StepEnum.SelectAgreement);
      return;
    }
    setStep(StepEnum.TermsAndConditions);
  };

  const isNextStepDisabled =
    (step === StepEnum.SelectDealer && !businessPartner) ||
    (step === StepEnum.SelectAgreement && (!agreementType || !isAgreementAcknowledged));

  return (
    <TeModal
      open={open}
      closeable
      modalTitle={headerTitle}
      closeMode="button"
      contentPadding={0}
      teclose={closeModal}
      footerText={footerText}
    >
      <TeContainer
        column
        grow
        backgroundColor="background.primary"
        gap="xs"
        alignItems="center"
        padding="s"
        style={{ maxWidth: MODAL_MAX_WIDTH }}
      >
        {step === StepEnum.SelectDealer && (
          <SelectDealerStep
            filters={filters}
            onFiltersChange={handleFiltersChange}
            providers={dealers}
            loading={isLoading}
            selectedDealer={businessPartner}
            onSelect={(bp) => {
              setBusinessPartner(bp);
            }}
          />
        )}
        {step === StepEnum.SelectAgreement && (
          <SelectAgreementTypeStep
            setType={setAgreementType}
            type={agreementType}
            onAcknowledgedChange={setIsAgreementAcknowledged}
          />
        )}
        {step === StepEnum.TermsAndConditions && (
          <ReviewAgreementStep onChecked={(checked) => setEulaDisclaimerAccepted(checked)} />
        )}
      </TeContainer>

      <TeButton
        slot="tertiary"
        text={t('cancel', { ns: 'common' })}
        variant="neutral"
        teclick={closeModal}
        size="s"
        disabled={isPendingCreateServiceAgreementWithProvider}
      />

      {currentStep > 0 && (
        <TeButton
          slot="tertiary"
          text={t('step.previous', { ns: 'common' })}
          variant="neutral"
          teclick={handleNavigationBack}
          size="s"
          disabled={isPendingCreateServiceAgreementWithProvider}
        />
      )}
      {}
      {currentStep === totalSteps - 1 ? (
        <TeButton
          text={headerTitle}
          variant="primary"
          slot="primary"
          teclick={handleCreateServiceAgreement}
          size="s"
          disabled={isPendingCreateServiceAgreementWithProvider || !eulaDisclaimerAccepted}
        />
      ) : (
        <TeButton
          text={t('nextStep')}
          variant="primary"
          slot="primary"
          teclick={handleNextStep}
          size="s"
          disabled={isNextStepDisabled}
        />
      )}
    </TeModal>
  );
}

export default AddDealerModal;
